// ─── Profil → Gestion de l'application → Catégories ───────────────
// Demandé par Cédric le 15/08 :
//   • ajouter / enlever des catégories PERSONNALISÉES ;
//   • MASQUER les catégories par défaut sans jamais les modifier — jusqu'à
//     n'utiliser que du personnel si on veut ;
//   • des SOUS-CATÉGORIES, entièrement optionnelles.
//
// Rendu DANS le Profil via la prop `sections` d'EcranProfil (contrat Core) :
// pas de modale, pas d'écran à empiler — c'est de la configuration, elle vit
// où la configuration vit.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import { useUI, useT, useAuth, useEspace, confirmerSuppression } from '@mop/core';
import { ecouterConfigFinance, sauverConfigFinance } from './services/configFinance';
import { CONFIG_DEFAUT, idPerso, validerCategoriePerso } from './services/categoriesPerso';
import { CATEGORIES_SAISIE, definirCategoriesPerso } from './data/categories';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function SectionCategories() {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const { tenantId } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [config, setConfig] = useState(CONFIG_DEFAUT);
  const [nom, setNom] = useState('');
  const [emoji, setEmoji] = useState('');
  const [sousCatDe, setSousCatDe] = useState(null);   // id de la catégorie dépliée
  const [nouvelleSousCat, setNouvelleSousCat] = useState('');

  useEffect(() => ecouterConfigFinance(tenantId, (c) => {
    setConfig(c);
    definirCategoriesPerso(c.categoriesPerso);
  }), [tenantId]);

  const echec = (e) => Alert.alert(t('commun.erreur'), e?.message || '');
  const sauver = (patch) => sauverConfigFinance(tenantId, patch, user?.uid).catch(echec);

  const masquees = new Set(config.categoriesMasquees || []);
  const basculerMasque = (id) => {
    const n = new Set(masquees);
    if (n.has(id)) n.delete(id); else n.add(id);
    sauver({ categoriesMasquees: [...n] });
  };

  const ajouterPerso = () => {
    const v = validerCategoriePerso(nom, config);
    if (!v.ok) { Alert.alert(t('commun.erreur'), t(`finances.catErr_${v.motif}`)); return; }
    sauver({
      categoriesPerso: [...(config.categoriesPerso || []),
        { id: idPerso(), nom: nom.trim(), emoji: (emoji || '🏷️').trim(), sousCategories: [] }],
    });
    setNom(''); setEmoji('');
  };

  const retirerPerso = (c) => confirmerSuppression(t('commun.supprimer'), c.nom, () =>
    sauver({ categoriesPerso: (config.categoriesPerso || []).filter(x => x.id !== c.id) }));

  // Sous-catégories : sur une perso elles vivent dans son document ; sur un
  // défaut, dans la table dédiée — le défaut lui-même n'est jamais modifié.
  const sousCatsDe = (id) => {
    const perso = (config.categoriesPerso || []).find(x => x.id === id);
    if (perso) return perso.sousCategories || [];
    return (config.sousCategoriesDefaut || {})[id] || [];
  };
  const sauverSousCats = (id, liste) => {
    const perso = (config.categoriesPerso || []).find(x => x.id === id);
    if (perso) {
      sauver({ categoriesPerso: (config.categoriesPerso || [])
        .map(x => (x.id === id ? { ...x, sousCategories: liste } : x)) });
    } else {
      sauver({ sousCategoriesDefaut: { ...(config.sousCategoriesDefaut || {}), [id]: liste } });
    }
  };
  const ajouterSousCat = (id) => {
    const v = nouvelleSousCat.trim();
    if (!v) return;
    const actuelles = sousCatsDe(id);
    if (actuelles.some(x => x.toLowerCase() === v.toLowerCase())) return;
    sauverSousCats(id, [...actuelles, v]);
    setNouvelleSousCat('');
  };

  const LigneCategorie = ({ id, emoji: em, libelle, persoDoc }) => {
    const masquee = masquees.has(id);
    const ouverte = sousCatDe === id;
    const sousCats = sousCatsDe(id);
    return (
      <View>
        <View style={[s.ligne, masquee && { opacity: 0.45 }]}>
          <TouchableOpacity onPress={() => setSousCatDe(ouverte ? null : id)} hitSlop={HIT}>
            <Text style={s.chev}>{ouverte ? '▾' : '▸'}</Text>
          </TouchableOpacity>
          <Text style={{ fontSize: 17 }}>{em}</Text>
          <Text style={s.nom} numberOfLines={1}>{libelle}</Text>
          {sousCats.length > 0 && <Text style={s.nb}>{sousCats.length}</Text>}
          <View style={{ flex: 1 }} />
          {persoDoc ? (
            <TouchableOpacity onPress={() => retirerPerso(persoDoc)} hitSlop={HIT}
              accessibilityLabel={t('commun.supprimer')}>
              <Text style={s.action}>✕</Text>
            </TouchableOpacity>
          ) : (
            /* 👁 bascule le masquage. Les défauts ne se suppriment JAMAIS :
               une catégorie par défaut masquée se démasque à l'identique. */
            <TouchableOpacity onPress={() => basculerMasque(id)} hitSlop={HIT}
              accessibilityLabel={t(masquee ? 'finances.catAfficher' : 'finances.catMasquer')}>
              <Text style={s.action}>{masquee ? '🙈' : '👁️'}</Text>
            </TouchableOpacity>
          )}
        </View>
        {ouverte && (
          <View style={s.sousZone}>
            {sousCats.map(sc => (
              <View key={sc} style={s.sousLigne}>
                <Text style={s.sousNom}>{sc}</Text>
                <TouchableOpacity hitSlop={HIT}
                  onPress={() => sauverSousCats(id, sousCats.filter(x => x !== sc))}>
                  <Text style={s.action}>✕</Text>
                </TouchableOpacity>
              </View>
            ))}
            <View style={s.sousAjout}>
              <TextInput style={s.sousInput} value={nouvelleSousCat}
                onChangeText={setNouvelleSousCat}
                placeholder={t('finances.sousCatPh')} placeholderTextColor={UI.faint} />
              <TouchableOpacity onPress={() => ajouterSousCat(id)} hitSlop={HIT}>
                <Text style={[s.action, { color: UI.accent }]}>＋</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    );
  };

  return (
    <View>
      {/* Les PERSONNALISÉES d'abord : ce sont les siennes. */}
      {(config.categoriesPerso || []).map(c => (
        <LigneCategorie key={c.id} id={c.id} emoji={c.emoji || '🏷️'} libelle={c.nom} persoDoc={c} />
      ))}

      <View style={s.ajout}>
        <TextInput style={[s.input, { width: 52, textAlign: 'center' }]} value={emoji}
          onChangeText={setEmoji} placeholder="🏷️" placeholderTextColor={UI.faint} maxLength={4} />
        <TextInput style={[s.input, { flex: 1 }]} value={nom} onChangeText={setNom}
          placeholder={t('finances.catNouvellePh')} placeholderTextColor={UI.faint} maxLength={30} />
        <TouchableOpacity style={s.btnAjout} onPress={ajouterPerso} activeOpacity={0.7}>
          <Text style={s.btnAjoutTxt}>＋</Text>
        </TouchableOpacity>
      </View>

      <Text style={s.titre}>{t('finances.catDefauts')}</Text>
      <Text style={s.aide}>{t('finances.catDefautsAide')}</Text>
      {CATEGORIES_SAISIE.map(c => (
        <LigneCategorie key={c.id} id={c.id} emoji={c.emoji}
          libelle={t(`budget.cat_${c.id}`)} />
      ))}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 9, paddingHorizontal: 4 },
  chev: { fontSize: 12, color: UI.accent, fontWeight: '800', width: 14 },
  nom: { fontSize: 14.5, color: UI.ink, fontWeight: '700', maxWidth: '55%' },
  nb: { fontSize: 11.5, color: UI.faint, fontWeight: '700' },
  action: { fontSize: 15, color: UI.muted, fontWeight: '700' },
  ajout: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6, marginBottom: 10 },
  input: { backgroundColor: UI.card, borderRadius: 10, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 9, paddingHorizontal: 10, fontSize: 14, color: UI.ink },
  btnAjout: { width: 40, height: 40, borderRadius: 10, borderWidth: 1, borderColor: UI.accent, alignItems: 'center', justifyContent: 'center' },
  btnAjoutTxt: { fontSize: 18, color: UI.accent, fontWeight: '700' },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 12 },
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginTop: 3, marginBottom: 4 },
  sousZone: { marginLeft: 40, marginBottom: 6 },
  sousLigne: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6, paddingHorizontal: 10, backgroundColor: UI.card, borderRadius: 9, borderWidth: 0.5, borderColor: UI.border, marginBottom: 5 },
  sousNom: { fontSize: 13, color: UI.ink, fontWeight: '600' },
  sousAjout: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sousInput: { flex: 1, backgroundColor: UI.card, borderRadius: 9, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 7, paddingHorizontal: 10, fontSize: 13, color: UI.ink },
});
